import type { LineSlug } from '../lines';
import type { Scene } from './types';

/**
 * Loaded on demand — the backdrops are hundreds of KB of SVG or baked WebP
 * each, and a rider only ever sees the lines their own network reaches.
 *
 * Some lines kept more than one backdrop from the redraw:
 *  - `original`  the first-generation concept art (design/originals-v1)
 *  - `remaster`  the same scene, repainted (design/scenes-v2/<slug>-remaster)
 *  - `alt`       a new scene for the line (design/scenes-v2/<slug>-alt)
 * Lines added in the redraw have one, `new`. Riders always see the first
 * listed; the rest are only reachable from the dev preview and picker.
 */
export type SceneVariant = 'original' | 'remaster' | 'alt' | 'new';

type Loader = () => Promise<{ default: Scene }>;

export const VARIANT_LABEL: Record<SceneVariant, string> = {
  original: '原画 ORIGINAL',
  remaster: '改 REMASTER',
  alt: '新 NEW SCENE',
  new: '新 NEW LINE',
};

const VARIANTS: Record<LineSlug, [SceneVariant, Loader][]> = {
  romance: [
    ['remaster', () => import('./v2/romance-remaster')],
    ['alt', () => import('./v2/romance-alt')],
    ['original', () => import('./romance')],
  ],
  action: [
    ['remaster', () => import('./v2/action-remaster')],
    ['alt', () => import('./v2/action-alt')],
    ['original', () => import('./action')],
  ],
  fantasy: [
    ['original', () => import('./fantasy')],
    ['remaster', () => import('./v2/fantasy-remaster')],
    ['alt', () => import('./v2/fantasy-alt')],
  ],
  comedy: [
    ['original', () => import('./comedy')],
    ['remaster', () => import('./v2/comedy-remaster')],
    ['alt', () => import('./v2/comedy-alt')],
  ],
  // Crossed on purpose: the level-crossing scene was built under the `-alt`
  // id before it was clear that the original LOCAL LINE scene is a crossing,
  // so it is the remaster; the shotengai built as `-remaster` is the new one.
  'slice-of-life': [
    ['remaster', () => import('./v2/slice-of-life-alt')],
    ['alt', () => import('./v2/slice-of-life-remaster')],
    ['original', () => import('./slice-of-life')],
  ],
  'sci-fi': [
    ['remaster', () => import('./v2/sci-fi-remaster')],
    ['alt', () => import('./v2/sci-fi-alt')],
    ['original', () => import('./sci-fi')],
  ],
  mystery: [
    ['remaster', () => import('./v2/mystery-remaster')],
    ['original', () => import('./mystery')],
    ['alt', () => import('./v2/mystery-alt')],
  ],
  horror: [
    ['remaster', () => import('./v2/horror-remaster')],
    ['alt', () => import('./v2/horror-alt')],
    ['original', () => import('./horror')],
  ],
  sports: [
    ['remaster', () => import('./v2/sports-remaster')],
    ['alt', () => import('./v2/sports-alt')],
    ['original', () => import('./sports')],
  ],
  isekai: [['new', () => import('./v2/isekai-new')]],
  mecha: [['new', () => import('./v2/mecha-new')]],
  music: [['new', () => import('./v2/music-new')]],
  supernatural: [['new', () => import('./v2/supernatural-new')]],
};

export function variantsOf(slug: LineSlug): SceneVariant[] {
  return VARIANTS[slug].map(([v]) => v);
}

/**
 * Which backdrop a line shows: the line's default, unless a dev build is
 * asked for another with `?scene=<variant>` (the preview and picker do).
 */
export function chosenVariant(slug: LineSlug): SceneVariant {
  const all = variantsOf(slug);
  const asked = import.meta.env.DEV ? new URLSearchParams(window.location.search).get('scene') : null;
  return asked && (all as string[]).includes(asked) ? (asked as SceneVariant) : all[0]!;
}

const cache = new Map<string, Promise<Scene>>();

/** Resolve once the scene's baked layers are decoded, so it never paints half-loaded. */
function warm(scene: Scene): Promise<Scene> {
  const urls = scene.assets ?? [];
  if (!urls.length) return Promise.resolve(scene);
  const one = (src: string) =>
    new Promise<void>((done) => {
      const img = new Image();
      img.src = src;
      // decode() also waits for decoding, not just the download; anything
      // without it (older engines, jsdom) settles on load or error instead.
      if (typeof img.decode === 'function') img.decode().then(done, done);
      else img.onload = img.onerror = () => done();
    });
  // A slow network should still get a backdrop: give up waiting after 4 s.
  return Promise.race([
    Promise.all(urls.map(one)),
    new Promise<void>((done) => window.setTimeout(done, 4000)),
  ]).then(() => scene);
}

export function loadScene(slug: LineSlug, variant: SceneVariant = chosenVariant(slug)): Promise<Scene> {
  const key = `${slug}:${variant}`;
  let hit = cache.get(key);
  if (!hit) {
    const entry = VARIANTS[slug].find(([v]) => v === variant) ?? VARIANTS[slug][0]!;
    hit = entry[1]().then((m) => warm(m.default));
    cache.set(key, hit);
  }
  return hit;
}

/** Warm the next station's backdrop while the rider is still reading this one. */
export function prefetchScene(slug: LineSlug): void {
  void loadScene(slug);
}

export type { Scene };
